<template>
  <b-modal v-if="canRegister"
           id="external-registration-modal"
           :visible="isOpen"
           lazy
           centered
           ok-variant="ls"
           ok-title="Zarejestruj"
           cancel-title="Anuluj"
           @ok="register"
           :ok-disabled="isBusy"
           @hidden="closeRegistrationModal"
           @show="onShow"
           size="lg">
    <template #modal-title>
      <h5>Rejestracja</h5>
      <p class="mb-0">
        <small>Wypełnij formularz, aby się zarejestrować.</small>
      </p>
    </template>
    <card-number-input v-if="allowEnteringNumber || registration.allowExistingNumbers"
                       ref="numberInput"
                       class="form-group m-form__group mx-4"
                       :required="allowEnteringNumber || !registration.allowExistingNumbers"
                       v-model="number" @invalid="state.number = false" @valid="state.number = true"/>
    <div class="form-group m-form__group row mt-5 mx-4">
      <label class="col-1 label-switch">
        <switches v-model="requiredAgreement"
                  v-b-tooltip.hover.left
                  @input="$root.$emit('bv::hide::tooltip')"
                  :title="requiredAgreement ? 'Zgoda udzielona' : 'Brak zgody'"
                  :color="requiredAgreement ? 'green' : 'default'"></switches>
      </label>
      <div class="col-11">
            <span v-if="state.agreement === false" class="small text-danger">
              Musisz zaznaczyć zgodę
            </span>
        <div class="text-justify">
          <required-agreement></required-agreement>
        </div>
      </div>
    </div>
    <div v-for="agreement in agreements" :key="agreement.number"
         class="form-group m-form__group row mt-2 mx-4">
      <label class="col-1 label-switch">
        <switches v-model="agreement.enabled"
                  v-b-tooltip.hover.left
                  @input="onAgreementInput(agreement.number)"
                  :title="agreement.enabled ? 'Zgoda udzielona' : 'Brak zgody'"
                  :color="agreement.enabled ? 'green' : 'default'"></switches>
      </label>
      <div class="col-11">
        <span v-if="state.additionalAgreements[agreement.number] === false" class="small text-danger">
              Musisz zaznaczyć zgodę
        </span>
        <agreement-text :agreement="agreement"/>
      </div>
    </div>
  </b-modal>
</template>

<script>
import RequiredAgreement from "@/components/agreements/RequiredAgreement";
import {SELF_REGISTRATION_ENTER_NUMBER, SELF_REGISTRATION_NO} from "@/utils/SelfRegistrationTypes";
import {mapActions, mapState} from "vuex";
import Switches from 'vue-switches';
import {isPwa} from "@/utils/Pwa";
import CardNumberInput from "@/components/input/CardNumberInput";
import {removeReflinkFields, setReflinkFields} from "@/utils/ReflinkFields";
import AgreementText from "@/components/agreements/AgreementText";

export default {
  name: "ExternalRegistrationModal",
  components: {
    AgreementText,
    CardNumberInput,
    RequiredAgreement,
    Switches,
  },
  data() {
    return {
      isBusy: false,
      number: '',
      requiredAgreement: false,
      state: {
        agreement: null,
        number: null,
        additionalAgreements: {}
      },
      agreements: [],
      hasAdditionalRequiredAgreementsGiven: false,
      agreementsValidated: false,
      lastChangedAgreementNumber: null,
    }
  },
  mounted() {
    this.cloneAgreementsFormState();
  },
  methods: {
    ...mapActions([
      'closeRegistrationModal',
      'afterLoginSuccess',
      'fetchSettings',
    ]),
    register(e) {
      e.preventDefault();
      this.isBusy = true;

      if (this.allowEnteringNumber) {
        this.$refs.numberInput.validate();
      }

      this.validateAdditionalAgreements();

      if (!this.requiredAgreement) {
        this.state.agreement = false;
      }

      if (!this.requiredAgreement || !this.hasAdditionalRequiredAgreementsGiven) {
        this.isBusy = false;
        return;
      }

      if (this.allowEnteringNumber && !this.state.number) {
        this.isBusy = false;
        return;
      }

      let form = {
        entityUuid: this.entityUuid,
        registrationToken: this.registrationToken,
        pwa: isPwa,
        number: this.number,
      };

      this.agreements.forEach((agreement) => {
        if (agreement.enabled) {
          form[`agreement${agreement.number}`] = true
        }
      });

      setReflinkFields(this.entityUuid, form);

      this.$http.post('register/token', form).then((r) => {
        if (r.data.close_register) {
          this.closeRegistrationModal();
        }

        if (r.data.success) {
          this.closeRegistrationModal();

          this.afterLoginSuccess({
            ...r.data,
            entity: form.entityUuid,
          })

          removeReflinkFields(this.entityUuid);
        } else {
          this.$notify.danger(r.data.message)
        }
      }).finally(() => {
        this.isBusy = false;
      });
    },
    cloneAgreementsFormState() {
      this.agreements = JSON.parse(JSON.stringify(this.stateAgreements))

      this.agreements.forEach((agreement) => {
        if (agreement.required === '1') {
          this.state.additionalAgreements[agreement.number] = null;
        }
      })
    },
    validateAdditionalAgreements() {
      this.agreementsValidated = true;
      this.hasAdditionalRequiredAgreementsGiven = true;

      this.agreements.forEach((agreement) => {
        if (agreement.required === '1' && !agreement.enabled) {
          this.hasAdditionalRequiredAgreementsGiven = false;
          this.state.additionalAgreements[agreement.number] = false;
        }
      })
    },
    onAgreementInput(number) {
      this.$root.$emit('bv::hide::tooltip');
      this.lastChangedAgreementNumber = number;
    },
    onShow() {
      //fetch settings for new agreements
      this.fetchSettings(this.entityUuid).then(() => this.cloneAgreementsFormState());
    }
  },
  computed: {
    ...mapState({
      entityUuid: state => state.auth.entity.uuid,
      allowEnteringNumber: state => state.settings.selfRegistration === SELF_REGISTRATION_ENTER_NUMBER,
      canRegister: state => state.settings.selfRegistration !== SELF_REGISTRATION_NO,
      isOpen: state => state.externalRegistrationModal.isOpen,
      registrationToken: state => state.externalRegistrationModal.registrationToken,
      stateAgreements: state => state.auth.agreements,
      clientNumberLabel: state => state.settings.clientNumberLabel,
      registration: state => state.settings.registration,
    }),
  },
  watch: {
    requiredAgreement(newVal) {
        this.state.agreement = newVal;
    },
    agreements: {
      deep: true,
      handler: function (val) {
        if (!this.agreementsValidated) {
          return;
        }

        val.forEach((agreement) => {
          if (agreement.number !== this.lastChangedAgreementNumber) {
            return;
          }

          if (agreement.required === '1') {
            this.state.additionalAgreements[agreement.number] = agreement.enabled;
          }
        })

        //reactivity issues
        this.$set(this.state, 'additionalAgreement', this.state.additionalAgreements);
      }
    },
  }
}
</script>

<style scoped>

</style>
